import { useMemo } from "react";
import * as React from "react";
import classNames from "clsx";

export type BannerVariant = "info" | "success" | "warning" | "error" | "light" | "dark";

interface BannerProps {
  className?: string;
  variant?: BannerVariant;
  showIcon?: boolean;
  title?: string;
  message?: string | React.ReactElement;
  onClose?: () => void;
}

export const Banner: React.FC<BannerProps> = ({
  variant = "light",
  showIcon,
  title,
  message,
  onClose,
  className: containerClassName,
}) => {
  const iconClassName = useMemo(() => {
    switch (variant) {
      case "info":
      case "error":
        return "icon-lt-information-line";
      case "success":
        return "icon-lt-check-line";
      case "warning":
        return "icon-lt-alert-line";
      case "light":
      case "dark":
        return "icon-lt-question-line";
    }
  }, [variant]);
  const iconColorClassName = useMemo(() => {
    switch (variant) {
      case "info":
        return "text-text-base-active";
      case "success":
        return "text-background-surface-success-accent";
      case "warning":
        return "text-background-surface-warning-accent";
      case "error":
        return "text-background-surface-destructive-accent";
      case "light":
        return "text-text-base-primary";
      case "dark":
        return "text-white";
    }
  }, [variant]);

  const titleColorClassName = useMemo(() => {
    return variant === "dark" ? "text-white" : "";
  }, [variant]);
  const messageColorClassName = useMemo(() => {
    return variant === "dark" ? "text-white" : "text-text-base-secondary-a";
  }, [variant]);
  const borderColorClassName = useMemo(() => {
    switch (variant) {
      case "info":
        return "border-border-action-info";
      case "success":
        return "border-border-action-success";
      case "warning":
        return "border-border-action-warning";
      case "error":
        return "border-border-action-destructive";
      case "light":
        return "border-border-action";
      case "dark":
        return "border-neutral-800-action";
    }
  }, [variant]);
  const backgroundColorClassName = useMemo(() => {
    switch (variant) {
      case "info":
        return "bg-platform-100";
      case "success":
        return "bg-positive-50";
      case "warning":
        return "bg-warning-50";
      case "error":
        return "bg-negative-50";
      case "light":
        return "bg-background-surface-neutral";
      case "dark":
        return "bg-neutral-500";
    }
  }, [variant]);
  return (
    <div
      data-testid="banner-container"
      className={classNames(
        "flex",
        "flex-row",
        "items-center",
        "rounded-lg",
        "border",
        "border-solid",
        "px-3",
        "py-2",
        "gap-x-3",
        backgroundColorClassName,
        borderColorClassName,
        containerClassName
      )}
    >
      {showIcon && (
        <i
          data-testid="banner-icon"
          className={classNames(iconClassName, "text-lg", "self-start", iconColorClassName)}
        />
      )}
      <div className={classNames("grow", "flex", "flex-col", "gap-y-2")} data-testid="banner-content">
        {title && (
          <div
            data-testid="banner-title"
            className={classNames("font-medium", "leading-4", "text-sm", titleColorClassName)}
          >
            {title}
          </div>
        )}
        {message && (
          <div
            data-testid="banner-message"
            className={classNames("font-regular", "leading-4", "text-xs", messageColorClassName)}
          >
            {message}
          </div>
        )}
      </div>
      {onClose && (
        <i
          data-testid="banner-close"
          className={classNames(
            "icon-lt-close-line",
            "text-lg",
            "self-start",
            "hover:cursor-pointer",
            messageColorClassName
          )}
          onClick={onClose}
        />
      )}
    </div>
  );
};
